import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import LessonSummaryApiUtils from '@utils/api/lessonSummary';
import LessonSummaryRatingApiUtils from '@utils/api/lessonSummaryRating';
import type {
  ShowSummaryModalContainerProps,
  Summary,
} from './show-summary-modal.types';

const getRateTextKey = (rate: number): string => {
  const rateKeyMap: Record<number, string> = {
    4: 'veryUseful',
    3: 'useful',
    2: 'littleUseful',
    1: 'notUseful',
  };
  return rateKeyMap[rate] || '';
};

export const useShowSummaryModalService = (
  props: ShowSummaryModalContainerProps,
) => {
  const { hideModal, addModalMessage, clearModalMessage, summaryId } = props;
  const { t } = useTranslation('common');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [selectedRate, setSelectedRate] = useState<number | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const data = await LessonSummaryApiUtils.fetchOne(summaryId);
        setSummary(data);
      } catch (error) {
        addModalMessage(t('modals.showSummary.errors.loadFailed'), {
          type: 'error',
          autoDisappear: false,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [summaryId, addModalMessage, t]);

  const handleRateChange = useCallback((value: number) => {
    setSelectedRate(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (selectedRate === null) {
        addModalMessage(t('modals.showSummary.errors.selectRating'), {
          type: 'error',
          autoDisappear: false,
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        await LessonSummaryRatingApiUtils.create(
          summaryId,
          selectedRate,
        );
        setSummary((prev) =>
          prev ? { ...prev, my_rating: { rate: selectedRate } } : null,
        );
        addModalMessage(t('modals.showSummary.success.rated'), {
          type: 'success',
          autoDisappear: true,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : t('modals.showSummary.errors.rateFailed');
        addModalMessage(errorMessage, {
          type: 'error',
          autoDisappear: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedRate, summaryId, addModalMessage, clearModalMessage, t],
  );

  // Determine if we should show rating form
  const showRatingForm =
    summary && !summary.my_summary && summary.my_rating === null;

  // Get translated rating text for existing rating
  const existingRatingText =
    summary?.my_rating !== null && !summary?.my_summary
      ? t(`modals.showSummary.ratingOptions.${getRateTextKey(summary.my_rating.rate)}`)
      : null;

  return {
    isLoading,
    isSubmitting,
    content: summary?.content || t('general.loading'),
    assignment: summary?.homework || t('general.loading'),
    nextLesson: summary?.next_lesson || t('general.loading'),
    showRatingForm: showRatingForm || false,
    existingRatingText,
    selectedRate,
    message: props.message,
    messageType: props.messageType,
    onRateChange: handleRateChange,
    onSubmit: handleSubmit,
    onClose: hideModal,
  };
};
